import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
    ClipboardList, Check, X, User, Clock, AlertCircle, FileText, Plus,
    Pin, Megaphone, Trash2, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ManagerNoticesSection({ isResidential = false }) {
    const { token } = useAuth();
    const [pendingNotices, setPendingNotices] = useState([]);
    const [publishedNotices, setPublishedNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);
    const [activeTab, setActiveTab] = useState('pending');
    
    // Create notice modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [newNotice, setNewNotice] = useState({
        title: '',
        content: '',
        priority: 'normal',
        is_pinned: false
    });

    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchNotices();
    }, []);

    const fetchNotices = async () => {
        setLoading(true);
        try {
            const [pendingRes, publishedRes] = await Promise.all([
                axios.get(`${API}/manager/community/notices/pending`, { headers }),
                axios.get(`${API}/manager/community/notices/published`, { headers })
            ]);
            setPendingNotices(pendingRes.data);
            setPublishedNotices(publishedRes.data);
        } catch (error) {
            console.error('Error fetching notices:', error);
            toast.error('Failed to load notices');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (noticeId) => {
        setProcessingId(noticeId);
        try {
            await axios.put(`${API}/manager/community/notices/${noticeId}/approve`, {}, { headers });
            toast.success('Notice approved and published!');
            fetchNotices();
        } catch (error) {
            console.error('Error approving notice:', error);
            toast.error('Failed to approve notice');
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (noticeId) => {
        setProcessingId(noticeId);
        try {
            await axios.delete(`${API}/manager/community/notices/${noticeId}`, { headers });
            toast.success('Notice deleted');
            fetchNotices();
        } catch (error) {
            console.error('Error deleting notice:', error);
            toast.error('Failed to delete notice');
        } finally {
            setProcessingId(null);
        }
    };

    const handleCreateNotice = async () => {
        if (!newNotice.title || !newNotice.content) {
            toast.error('Please fill in title and content');
            return;
        }
        
        setCreateLoading(true);
        try {
            await axios.post(`${API}/manager/community/notices`, newNotice, { headers });
            toast.success('Notice published to community!');
            setShowCreateModal(false);
            setNewNotice({ title: '', content: '', priority: 'normal', is_pinned: false });
            fetchNotices();
            setActiveTab('published');
        } catch (error) {
            console.error('Error creating notice:', error);
            toast.error('Failed to create notice');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleDeletePublished = async (noticeId) => {
        if (!window.confirm('Are you sure you want to delete this notice?')) return;
        
        setProcessingId(noticeId);
        try {
            await axios.delete(`${API}/manager/community/notices/${noticeId}`, { headers });
            toast.success('Notice deleted');
            fetchNotices();
        } catch (error) {
            console.error('Error deleting notice:', error);
            toast.error('Failed to delete notice');
        } finally {
            setProcessingId(null);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return date.toLocaleString('en-GB', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getPriorityBadge = (priority) => {
        switch (priority) {
            case 'urgent':
                return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">Urgent</span>;
            case 'important':
                return <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">Important</span>;
            default:
                return null;
        }
    };

    return (
        <div className="space-y-6" data-testid="manager-notices-section">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <ClipboardList className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-stone-800">Community Notices</h2>
                        <p className="text-sm text-stone-500">Post announcements and manage member submissions</p>
                    </div>
                </div>
                <Button 
                    onClick={() => setShowCreateModal(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="create-notice-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Post Notice
                </Button>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="pending" className="relative">
                        Pending Approval
                        {pendingNotices.length > 0 && (
                            <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                                {pendingNotices.length}
                            </span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="published">
                        Published ({publishedNotices.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="pending">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : pendingNotices.length === 0 ? (
                        <div className="bg-stone-50 rounded-xl p-8 text-center border border-stone-200">
                            <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                            <h3 className="font-semibold text-stone-700 mb-2">No Pending Notices</h3>
                            <p className="text-stone-500 text-sm">
                                All community notices have been reviewed. New submissions from members will appear here.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="text-sm text-amber-800 font-medium">
                                        {pendingNotices.length} notice{pendingNotices.length !== 1 ? 's' : ''} pending review
                                    </p>
                                    <p className="text-xs text-amber-600 mt-1">
                                        Approved notices will be published to the community noticeboard.
                                    </p>
                                </div>
                            </div>

                            {pendingNotices.map((notice) => (
                                <div 
                                    key={notice.id} 
                                    className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm"
                                    data-testid={`pending-notice-${notice.id}`}
                                >
                                    <div className="p-5">
                                        <div className="flex items-start justify-between gap-4 mb-4">
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-stone-800 text-lg">{notice.title}</h3>
                                                <div className="flex items-center gap-3 mt-1 text-sm text-stone-500">
                                                    <div className="flex items-center gap-1">
                                                        <User className="w-4 h-4" />
                                                        <span>{notice.submitted_by_name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-4 h-4" />
                                                        <span>{formatDate(notice.created_at)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                                Pending
                                            </span>
                                        </div>
                                        
                                        <div className="bg-stone-50 rounded-lg p-4 mb-4">
                                            <p className="text-stone-700 whitespace-pre-wrap">{notice.content}</p>
                                        </div>

                                        <div className="flex items-center gap-3 justify-end">
                                            <Button
                                                variant="outline"
                                                onClick={() => handleReject(notice.id)}
                                                disabled={processingId === notice.id}
                                                className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                                                data-testid={`reject-notice-${notice.id}`}
                                            >
                                                {processingId === notice.id ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <X className="w-4 h-4 mr-2" />
                                                )}
                                                Delete
                                            </Button>
                                            <Button
                                                onClick={() => handleApprove(notice.id)}
                                                disabled={processingId === notice.id}
                                                className="bg-emerald-600 hover:bg-emerald-700"
                                                data-testid={`approve-notice-${notice.id}`}
                                            >
                                                {processingId === notice.id ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Check className="w-4 h-4 mr-2" />
                                                )}
                                                Approve & Publish
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="published">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : publishedNotices.length === 0 ? (
                        <div className="bg-stone-50 rounded-xl p-8 text-center border border-stone-200">
                            <Megaphone className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                            <h3 className="font-semibold text-stone-700 mb-2">No Published Notices</h3>
                            <p className="text-stone-500 text-sm mb-4">
                                Start sharing updates with your community.
                            </p>
                            <Button onClick={() => setShowCreateModal(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Post First Notice
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {publishedNotices.map((notice) => (
                                <div 
                                    key={notice.id} 
                                    className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm"
                                    data-testid={`published-notice-${notice.id}`}
                                >
                                    <div className="p-5">
                                        <div className="flex items-start justify-between gap-4 mb-3">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-semibold text-stone-800 text-lg">{notice.title}</h3>
                                                    {notice.is_pinned && (
                                                        <Pin className="w-4 h-4 text-emerald-600" />
                                                    )}
                                                    {getPriorityBadge(notice.priority)}
                                                </div>
                                                <div className="flex items-center gap-3 text-sm text-stone-500">
                                                    <div className="flex items-center gap-1">
                                                        <User className="w-4 h-4" />
                                                        <span>{notice.submitted_by_name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-4 h-4" />
                                                        <span>{formatDate(notice.created_at)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                                                Published
                                            </span>
                                        </div>
                                        
                                        <div className="bg-stone-50 rounded-lg p-4 mb-4">
                                            <p className="text-stone-700 whitespace-pre-wrap">{notice.content}</p>
                                        </div>

                                        <div className="flex items-center justify-end">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleDeletePublished(notice.id)}
                                                disabled={processingId === notice.id}
                                                className="text-red-600 border-red-200 hover:bg-red-50"
                                                data-testid={`delete-notice-${notice.id}`}
                                            >
                                                {processingId === notice.id ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                )}
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Create Notice Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Post Community Notice</DialogTitle>
                        <DialogDescription>
                            Share an announcement with all residents. This will be published immediately.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="notice-title">Title *</Label>
                            <Input
                                id="notice-title"
                                value={newNotice.title}
                                onChange={(e) => setNewNotice(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g., Garden Party This Saturday"
                                data-testid="notice-title-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="notice-content">Content *</Label>
                            <Textarea
                                id="notice-content"
                                value={newNotice.content}
                                onChange={(e) => setNewNotice(prev => ({ ...prev, content: e.target.value }))}
                                placeholder="Write your announcement here..."
                                rows={5}
                                data-testid="notice-content-input"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Priority</Label>
                                <Select 
                                    value={newNotice.priority} 
                                    onValueChange={(v) => setNewNotice(prev => ({ ...prev, priority: v }))}
                                >
                                    <SelectTrigger data-testid="notice-priority-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="normal">Normal</SelectItem>
                                        <SelectItem value="important">Important</SelectItem>
                                        <SelectItem value="urgent">Urgent</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                                <div>
                                    <p className="font-medium text-sm">Pin to Top</p>
                                    <p className="text-xs text-muted-foreground">Keep visible</p>
                                </div>
                                <Switch
                                    checked={newNotice.is_pinned}
                                    onCheckedChange={(checked) => setNewNotice(prev => ({ ...prev, is_pinned: checked }))}
                                    data-testid="notice-pin-toggle"
                                />
                            </div>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleCreateNotice}
                            disabled={!newNotice.title || !newNotice.content || createLoading}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="publish-notice-btn"
                        >
                            {createLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Publishing...
                                </>
                            ) : (
                                <>
                                    <Megaphone className="w-4 h-4 mr-2" />
                                    Publish Notice
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ManagerNoticesSection;
